<!-- Breadcrumb is now handled by the shared header component -->

<!-- Main Content -->
<div class="main-content container">
  <!-- Sidebar -->
  <aside class="sidebar" id="sidebar">
    <div class="sidebar-title">Quick Links</div>
    <ul class="sidebar-menu">
      <li><a href="#" (click)="$event.preventDefault(); showSection('dashboard')"
        [class.active]="activeSection === 'dashboard'"><i class="fas fa-home"></i> Dashboard</a></li>

      <!-- Only shown while an application is still being processed. -->
      @if (hasOpenApplication) {
        <li><a href="#" (click)="$event.preventDefault(); showSection('applications')"
          [class.active]="activeSection === 'applications'"><i class="fas fa-file-alt"></i> Applications</a></li>
      }

      <!-- Award-driven sections: only active once a scholarship is awarded. -->
      @if (hasActiveAward || isAlumniView) {
        <li><a href="#" (click)="$event.preventDefault(); showSection('flights')"
          [class.active]="activeSection === 'flights'"><i class="fas fa-plane"></i> Flight Booking</a></li>

        <!-- Budget, expenses and claims are tabs on the one funding page. -->
        <li><a href="#" (click)="$event.preventDefault(); showSection('budget')"
          [class.active]="activeSection === 'budget'"><i class="fas fa-chart-pie"></i> Budget &amp; Expenses</a></li>

        @if (hasActiveAward) {
          <li><a href="#" (click)="$event.preventDefault(); showSection('progress')"
            [class.active]="activeSection === 'progress'"><i class="fas fa-chart-line"></i> Progress Reports</a></li>
        }
      }

      <li><a href="#" (click)="$event.preventDefault(); showSection('documents')"
        [class.active]="activeSection === 'documents'"><i class="fas fa-file-download"></i> Documents</a></li>

      <li><a href="#" (click)="$event.preventDefault(); showSection('events')"
        [class.active]="activeSection === 'events'"><i class="fas fa-calendar-alt"></i> Events</a></li>

      <li><a href="#" (click)="$event.preventDefault(); showSection('partners')"
        [class.active]="activeSection === 'partners'"><i class="fas fa-handshake"></i> Partners</a></li>

      <li><a href="#" (click)="$event.preventDefault(); showSection('settings')"
        [class.active]="activeSection === 'settings'"><i class="fas fa-cog"></i> Settings</a></li>
    </ul>
  </aside>

                <!-- Content Area -->
                <main class="content-area">

                  <!-- Dashboard Content -->
                  @if (activeSection === 'dashboard') {
                    <div class="content-section">

                      @if (isLoadingDashboard) {
                        <div class="empty-state"><i class="fas fa-spinner fa-spin"></i> Loading your scholarship…</div>
                      }

                      @if (dashboardError) {
                        <div class="alert alert-warning">
                          <i class="fas fa-exclamation-circle"></i> {{ dashboardError }}
                          <button class="inline-link" (click)="loadDashboard()">Try again</button>
                        </div>
                      }

                      <!-- ============ Awarded: funded scholarship ============ -->
                      @if (activeAward; as award) {
                        <div class="card">
                          <h2 class="card-title">{{ award.scholarshipTitle }}</h2>
                          <div class="card-body">
                            <div class="award-header">
                              <span [class]="awardStatusClass(award.status)">{{ award.status }}</span>
                              <small>Award {{ award.referenceNumber }} · cycle {{ award.cycle }}</small>
                            </div>

                            <!-- Duration tracker -->
                            <h3 class="dashboard-heading">Scholarship duration</h3>
                            <p class="dashboard-caption">
                              {{ award.startDate | date:'mediumDate' }} — {{ award.endDate | date:'mediumDate' }}
                            </p>
                            <div class="progress-track">
                              <div class="progress-fill" [style.width]="progressWidth(award.durationProgressPercent)"></div>
                            </div>
                            <p class="dashboard-caption">
                              {{ award.monthsElapsed }} month{{ award.monthsElapsed === 1 ? '' : 's' }} elapsed,
                              <strong>{{ award.monthsRemaining }} month{{ award.monthsRemaining === 1 ? '' : 's' }} remaining</strong>
                            </p>

                            <!-- Budget tracker -->
                            <h3 class="dashboard-heading">Budget</h3>
                            <div class="budget-summary">
                              <div class="budget-figure">
                                <span class="budget-label">Allocated</span>
                                <span class="budget-value">{{ money(award.totalAllocation, award.currency) }}</span>
                              </div>
                              <div class="budget-figure">
                                <span class="budget-label">Used so far</span>
                                <span class="budget-value">{{ money(award.usedAmount, award.currency) }}</span>
                              </div>
                              <div class="budget-figure">
                                <span class="budget-label">Remaining</span>
                                <span class="budget-value">{{ money(award.remainingAmount, award.currency) }}</span>
                              </div>
                            </div>
                            <div class="progress-track">
                              <div class="progress-fill" [style.width]="progressWidth(award.budgetProgressPercent)"></div>
                            </div>
                            <p class="dashboard-caption">{{ award.budgetProgressPercent }}% of your allocation has been used.</p>

                            <!-- Scholarship benefits -->
                            <h3 class="dashboard-heading">Scholarship benefits</h3>
                            <div class="table-responsive">
                              <table class="table">
                                <thead>
                                  <tr>
                                    <th>Benefit</th>
                                    <th>Allocated</th>
                                    <th>Used</th>
                                    <th>Remaining</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  @if (award.benefits.length === 0) {
                                    <tr><td colspan="4" class="empty-cell">No benefits are configured on this award.</td></tr>
                                  }
                                  @for (benefit of award.benefits; track benefit.id) {
                                    <tr>
                                      <td>
                                        <strong>{{ benefit.benefitName }}</strong>
                                        @if (benefit.isRecurring) {
                                          <small>Recurring @if (benefit.payDirectToProvider) { · paid to provider }</small>
                                        }
                                      </td>
                                      <td>{{ money(benefit.allocatedAmount, award.currency) }}</td>
                                      <td>{{ money(benefit.usedAmount, award.currency) }}</td>
                                      <td>{{ money(benefit.remainingAmount, award.currency) }}</td>
                                    </tr>
                                  }
                                </tbody>
                              </table>
                            </div>

                            <!-- Activity summaries -->
                            <h3 class="dashboard-heading">Claims</h3>
                            <div class="stat-grid">
                              <div class="stat"><span class="stat-value">{{ dashboard?.claims?.total ?? 0 }}</span><span class="stat-label">Submitted</span></div>
                              <div class="stat"><span class="stat-value">{{ dashboard?.claims?.pending ?? 0 }}</span><span class="stat-label">Awaiting review</span></div>
                              <div class="stat"><span class="stat-value">{{ dashboard?.claims?.approved ?? 0 }}</span><span class="stat-label">Approved</span></div>
                              <div class="stat"><span class="stat-value">{{ dashboard?.claims?.paid ?? 0 }}</span><span class="stat-label">Paid</span></div>
                              <div class="stat"><span class="stat-value">{{ money(dashboard?.claims?.totalPaidAmount, award.currency) }}</span><span class="stat-label">Paid to date</span></div>
                            </div>

                            <h3 class="dashboard-heading">Flights</h3>
                            <div class="stat-grid">
                              <div class="stat"><span class="stat-value">{{ dashboard?.flights?.total ?? 0 }}</span><span class="stat-label">Requests</span></div>
                              <div class="stat"><span class="stat-value">{{ dashboard?.flights?.pending ?? 0 }}</span><span class="stat-label">Awaiting review</span></div>
                              <div class="stat"><span class="stat-value">{{ dashboard?.flights?.booked ?? 0 }}</span><span class="stat-label">Booked</span></div>
                            </div>

                            <h3 class="dashboard-heading">Progress reports</h3>
                            <p class="dashboard-caption">
                              {{ dashboard?.progress?.total ?? 0 }} submitted
                              @if (dashboard?.progress?.latestStatus) {
                                · latest <span class="status status-pending">{{ dashboard?.progress?.latestStatus }}</span>
                                @if (dashboard?.progress?.latestPeriod) { ({{ dashboard?.progress?.latestPeriod }}) }
                              }
                            </p>

                            <!-- Shortcut actions -->
                            <h3 class="dashboard-heading">Quick actions</h3>
                            <div class="card-actions">
                              <button type="button" class="btn btn-primary" (click)="showSection('flights')">
                                <i class="fas fa-plane"></i> Request a flight
                              </button>
                              <button type="button" class="btn btn-primary" (click)="showSection('claims')">
                                <i class="fas fa-file-invoice"></i> Submit a claim
                              </button>
                              <button type="button" class="btn btn-primary" (click)="showSection('progress')">
                                <i class="fas fa-chart-line"></i> Submit a progress report
                              </button>
                              <button type="button" class="btn btn-secondary" (click)="showSection('budget')">
                                <i class="fas fa-chart-pie"></i> View budget
                              </button>
                            </div>
                          </div>
                        </div>
                      }

                      <!-- ============ Completed: read-only history ============ -->
                      @if (isAlumniView) {
                        <div class="card">
                          <h2 class="card-title">Your completed scholarship</h2>
                          <div class="card-body">
                            <p class="dashboard-caption">
                              Your scholarship has been completed, so these records are now read-only.
                              You can still review your budget, claims, expenses and flights.
                            </p>

                            @if (pastAwards.length === 0) {
                              <div class="empty-state">
                                <i class="fas fa-graduation-cap"></i>
                                <h3>No completed awards found</h3>
                                <p>Your past scholarship details will appear here.</p>
                              </div>
                            }

                            @for (past of pastAwards; track past.id) {
                              <div class="past-award">
                                <div class="award-header">
                                  <strong>{{ past.scholarshipTitle }}</strong>
                                  <span [class]="awardStatusClass(past.status)">{{ past.status }}</span>
                                </div>
                                <div class="detail-grid">
                                  <div class="detail-item"><span class="detail-label">Award</span><span class="detail-value">{{ past.referenceNumber }}</span></div>
                                  <div class="detail-item"><span class="detail-label">Cycle</span><span class="detail-value">{{ past.cycle }}</span></div>
                                  <div class="detail-item"><span class="detail-label">Period</span><span class="detail-value">{{ past.startDate | date:'mediumDate' }} — {{ past.endDate | date:'mediumDate' }}</span></div>
                                  <div class="detail-item"><span class="detail-label">Allocated</span><span class="detail-value">{{ money(past.totalAllocation, past.currency) }}</span></div>
                                  <div class="detail-item"><span class="detail-label">Used</span><span class="detail-value">{{ money(past.usedAmount, past.currency) }}</span></div>
                                  <div class="detail-item"><span class="detail-label">Remaining</span><span class="detail-value">{{ money(past.remainingAmount, past.currency) }}</span></div>
                                </div>
                                <div class="card-actions">
                                  <button type="button" class="btn btn-secondary btn-sm" (click)="showSection('expenses')">
                                    Budget &amp; usage
                                  </button>
                                  <button type="button" class="btn btn-secondary btn-sm" (click)="showSection('flights')">
                                    Flight history
                                  </button>
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      }

                      <!-- Not awarded: keep tracking the application -->
                      @if (!isLoadingDashboard && !hasActiveAward && !isAlumniView) {
                      <div class="card">
                        <h2 class="card-title">My Scholarship Application Status</h2>
                        <div class="card-body">
                          @if (isLoadingApplications) {
                            <div class="empty-state"><i class="fas fa-spinner fa-spin"></i> Loading your application status…</div>
                          }
                          @if (!isLoadingApplications && !latestApplication) {
                            <div class="empty-state">
                              <i class="fas fa-clipboard-list"></i>
                              <h3>No applications yet</h3>
                              <p>Start an application to track its progress here.</p>
                              <button type="button" class="btn btn-primary" (click)="startNewApplication()">Start an application</button>
                            </div>
                          }
                          @if (!isLoadingApplications && latestApplication) {
                            <div class="progress-tracker">
                              <div class="progress-steps">
                                <div class="progress-bar" style="width: 35%;"></div>
                                <div class="step completed">
                                  <div class="step-number">1</div>
                                  <div class="step-label">Application</div>
                                </div>
                                <div class="step active">
                                  <div class="step-number">2</div>
                                  <div class="step-label">Review</div>
                                </div>
                                <div class="step">
                                  <div class="step-number">3</div>
                                  <div class="step-label">Approval</div>
                                </div>
                                <div class="step">
                                  <div class="step-number">4</div>
                                  <div class="step-label">Funding</div>
                                </div>
                              </div>
                              <div class="status" [class]="'status ' + statusClass(latestApplication)">{{ statusLabel(latestApplication) }}</div>
                              <p><strong>{{ latestApplication.scholarship?.title || 'Scholarship application' }}</strong> was submitted on {{ latestApplication.applicationDate | date:'mediumDate' }}. We will notify you when its status changes.</p>
                            </div>
                          }
                        </div>
                      </div>
                      }
                    </div>
                  }

                  <!-- Applications Content -->
                  @if (activeSection === 'applications') {
                    <div class="content-section">
                      <div class="card">
                        <h2 class="card-title">My Applications</h2>
                        <div class="card-body">
                          <div class="card-actions">
                            <a routerLink="/scholarships" class="btn btn-primary"><i class="fas fa-plus"></i> New
                          Application</a>
                        </div>
                        <div class="table-toolbar">
                          <span>{{ applications.length }} application{{ applications.length === 1 ? '' : 's' }}</span>
                          <label>Show
                            <select class="page-size-select" [(ngModel)]="pageSize" (ngModelChange)="changePageSize()">
                              @for (option of pageSizeOptions; track option) {
                                <option [ngValue]="option">{{ option }}</option>
                              }
                            </select>
                            per page
                          </label>
                        </div>
                        @if (applicationsError) {
                          <div class="alert alert-warning"><i class="fas fa-exclamation-circle"></i> {{ applicationsError }} <button class="inline-link" (click)="loadApplications()">Try again</button></div>
                        }
                        <div class="table-responsive">
                          <table class="table">
                            <thead>
                              <tr>
                                <th>Scholarship</th>
                                <th>Status</th>
                                <th>Date Applied</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              @if (isLoadingApplications) {
                                <tr><td colspan="4" class="empty-cell"><i class="fas fa-spinner fa-spin"></i> Loading applications…</td></tr>
                              }
                              @if (!isLoadingApplications && applications.length === 0) {
                                <tr><td colspan="4" class="empty-cell">No applications found. Start a new application to see it here.</td></tr>
                              }
                              @for (application of paginatedApplications; track application) {
                                <tr>
                                  <td><strong>{{ application.scholarship?.title || application.referenceNumber }}</strong>@if (application.referenceNumber) {
                                  <small>Reference: {{ application.referenceNumber }}</small>
                                }</td>
                                <td><span [class]="'status ' + statusClass(application)">{{ statusLabel(application) }}</span></td>
                                <td>{{ application.applicationDate | date:'mediumDate' }}</td>
                                <td><button class="btn btn-secondary btn-sm" (click)="openApplicationDetail(application)">View details</button></td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                      @if (applications.length > pageSize) {
                        <app-pagination [currentPage]="currentPage" [totalPages]="totalPages" [totalItems]="applications.length" [showingFrom]="showingFrom" [showingTo]="showingTo" itemLabel="applications" (pageChange)="currentPage = $event"></app-pagination>
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Documents Content -->
              @if (activeSection === 'documents') {
                <div class="content-section">
                  <div class="card">
                    <h2 class="card-title">My Documents</h2>
                    <div class="card-body">
                      @if (isLoadingDocuments) {
                        <div class="empty-state"><i class="fas fa-spinner fa-spin"></i> Loading documents…</div>
                      }
                      @if (documentsError) {
                        <div class="alert alert-warning"><i class="fas fa-exclamation-circle"></i> {{ documentsError }} <button class="inline-link" (click)="loadDocuments()">Try again</button></div>
                      }
                      @if (!isLoadingDocuments && documents.length === 0) {
                        <div class="empty-state">
                          <i class="fas fa-folder-open"></i>
                          <h3>No saved documents yet</h3>
                          <p>When you upload documents for a scholarship application, they will appear here so you can reuse them.</p>
                          <a routerLink="/scholarships" class="btn btn-primary">Find a scholarship to apply</a>
                        </div>
                      }
                      @if (!isLoadingDocuments && documents.length > 0) {
                        <p class="section-note">Documents you have uploaded with previous applications. You can download or reuse these when applying for a new scholarship.</p>
                        <div class="table-responsive">
                          <table class="table">
                            <thead>
                              <tr>
                                <th>Document Type</th>
                                <th>File Name</th>
                                <th>Uploaded</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (document of documents; track document.id) {
                                <tr>
                                  <td><span class="document-type-badge"><i class="fas fa-file-alt"></i> {{ document.documentTypeName }}</span></td>
                                  <td>{{ document.fileName }}</td>
                                  <td>{{ document.uploadedAt | date:'mediumDate' }}</td>
                                  <td><button class="btn btn-secondary btn-sm" (click)="downloadDocument(document)"><i class="fas fa-download"></i> Download</button></td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Flight Booking Content -->
              @if (activeSection === 'flights') {
                <div class="content-section">
                  <div class="card">
                    <h2 class="card-title">Flight Booking</h2>
                    <div class="card-body">
                      <app-flight-requests [embedded]="true"></app-flight-requests>
                    </div>
                  </div>
                </div>
              }

              <!-- Events Content -->
              @if (activeSection === 'events') {
                <div class="content-section">
                  <div class="card">
                    <h2 class="card-title">Events</h2>
                    <div class="card-body">
                      <app-student-events [embedded]="true" />
                    </div>
                  </div>
                </div>
              }

              <!-- Partners Content -->
              @if (activeSection === 'partners') {
                <div class="content-section">
                  <div class="card">
                    <h2 class="card-title">Partner Institutions</h2>
                    <div class="card-body">
                      <div class="partner-list">
                        <div class="partner-item">
                          <img src="https://www.up.ac.za/themes/up2.0/images/horizontal-logo-bg.png"
                            alt="University Logo" class="partner-logo">
                          <div class="partner-details">
                            <h3>University of Pretoria</h3>
                            <p>20 available scholarships for South African students</p>
                            <p><i class="fas fa-map-marker-alt"></i> Pretoria, ZA</p>
                          </div>
                        </div>
                        <div class="partner-item">
                          <div class="partner-logo partner-logo-placeholder">
                            <i class="fas fa-university"></i>
                          </div>
                          <div class="partner-details">
                            <h3>Tshwane University of Technology</h3>
                            <p>5 full scholarships for postgraduate studies</p>
                            <p><i class="fas fa-map-marker-alt"></i> Pretoria, ZA</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }

              <!-- Budget page: budget, expenses and claims as tabs -->
              @if (activeSection === 'budget') {
                <div class="content-section">
                  <div class="card">
                    <div class="card-header">Budget &amp; Expenses</div>
                    <div class="card-body">
                      <div class="tabs">
                        <button type="button" class="tab-btn" [class.active]="activeBudgetTab === 'budget'"
                          (click)="selectBudgetTab('budget')">Budget</button>
                        <button type="button" class="tab-btn" [class.active]="activeBudgetTab === 'expenses'"
                          (click)="selectBudgetTab('expenses')">Expenses</button>
                        @if (hasActiveAward) {
                          <button type="button" class="tab-btn" [class.active]="activeBudgetTab === 'claims'"
                            (click)="selectBudgetTab('claims')">Claims</button>
                        }
                      </div>

                      @if (activeBudgetTab === 'budget') {
                        <app-student-expenses mode="budget" />
                      }
                      @if (activeBudgetTab === 'expenses') {
                        <app-student-expenses mode="expenses" />
                      }
                      @if (activeBudgetTab === 'claims' && hasActiveAward) {
                        <app-student-claims />
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Progress reports Content -->
              @if (activeSection === 'progress') {
                <div class="content-section">
                  <app-student-progress-reports />
                </div>
              }

              <!-- Settings Content -->
              @if (activeSection === 'settings') {
                <div class="content-section">
                  <div class="card">
                    <h2 class="card-title">Account Settings</h2>
                    <div class="card-body" style="text-align:center; padding: 40px; color:var(--text-color);">
                      Account settings coming soon.
                    </div>
                  </div>
                </div>
              }

              <!-- Application detail modal -->
              @if (isApplicationDetailOpen && selectedApplication) {
                <div class="modal-backdrop" (click)="closeApplicationDetail()">
                  <div class="modal-dialog" (click)="$event.stopPropagation()">
                    <div class="modal-header">
                      <h2>Application Details</h2>
                      <button class="modal-close" (click)="closeApplicationDetail()"><i class="fas fa-times"></i></button>
                    </div>
                    <div class="modal-body">
                      <div class="detail-grid">
                        <div class="detail-item"><span class="detail-label">Reference</span><span class="detail-value">{{ selectedApplication.referenceNumber || '—' }}</span></div>
                        <div class="detail-item"><span class="detail-label">Scholarship</span><span class="detail-value">{{ selectedApplication.scholarship?.title || '—' }}</span></div>
                        <div class="detail-item"><span class="detail-label">Status</span><span class="detail-value"><span [class]="'status ' + statusClass(selectedApplication)">{{ statusLabel(selectedApplication) }}</span></span></div>
                        <div class="detail-item"><span class="detail-label">Date Applied</span><span class="detail-value">{{ selectedApplication.applicationDate | date:'medium' }}</span></div>
                        <div class="detail-item"><span class="detail-label">Last Updated</span><span class="detail-value">{{ selectedApplication.statusChangedDate ? (selectedApplication.statusChangedDate | date:'medium') : '—' }}</span></div>
                      </div>
                      <p class="detail-note">Status updates are sent to your registered email address. If you have any questions about this application, contact the scholarship administrator.</p>
                    </div>
                    <div class="modal-footer">
                      <button class="btn btn-secondary" (click)="closeApplicationDetail()">Close</button>
                    </div>
                  </div>
                </div>
              }
            </main>
          </div>
